import { ChangeEvent, useRef, useState } from 'react'
import { Avatar, FormHelperText, IconButton } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { ImageInputPopover } from './image-input-popover.component'
import { StockImagesModal } from '../stock-images'
import { ImagePreview } from './image-preview.component'
import { IMessageResponseDto, ISetStockImage } from 'src/types'

import { AppEvents, DEFAULT_ICON_URL } from 'src/constants'
import useStyles from './styles'
import { MoreButton } from './more-button.component'
import { Stub } from './stub.component'

interface IImageInputProps {
  image: string | null
  originalImage: string | null
  alt: string
  handleImageChange: (e: ChangeEvent<HTMLInputElement>) => void
  errorText?: string | null
  isRound?: boolean
  deleteImage?: () => Promise<void | IMessageResponseDto> | undefined | void
  handleStockImageSet: ISetStockImage
}

const ImageInput = function ({
  image,
  handleImageChange,
  handleStockImageSet,
  errorText,
  isRound,
  deleteImage,
  alt,
  originalImage,
}: IImageInputProps) {
  const [t] = useTranslation()
  const classes = useStyles({ isRoundImage: isRound })
  const [open, setOpen] = useState(false)
  const [imagesOpen, setImagesOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const handlePreviewOpen = () => setPreviewOpen(true)
  const handlePreviewClose = () => setPreviewOpen(false)
  const handleImagesOpen = () => {
    setImagesOpen(true)
    handleClose()
  }
  const handleImagesClose = () => setImagesOpen(false)

  const handleImagesSet: ISetStockImage = (img) => {
    handleStockImageSet(img)
    handleImagesClose()
  }

  const handleChange = () => {
    inputRef.current?.click()
    handleClose()
  }

  const handleDelete = async () => {
    handleClose()

    if (image) {
      const resp = await deleteImage?.()

      if (resp) {
        const detail = { message: resp.message }
        const event = new CustomEvent(AppEvents.HTTP_SUCCESS, { detail })
        document.dispatchEvent(event)
      }
    }

    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  return (
    <div>
      {image && (
        <div ref={anchorRef} className={classes.imageInputWrapper}>
          <IconButton onClick={handlePreviewOpen} className={classes.imageIconButton}>
            <Avatar alt={alt} src={image} className={classes.image} />
          </IconButton>
          <MoreButton handleOpen={handleOpen} />
        </div>
      )}
      {!image && typeof image !== 'undefined' && (
        <Stub handleImagesOpen={handleImagesOpen} isRound={isRound} inputRef={inputRef} />
      )}
      <FormHelperText className={classes.helperText} error={!!errorText}>
        {errorText && t(errorText)}
      </FormHelperText>
      <input
        name="mainImage"
        id="mainImage"
        accept="image/*"
        type="file"
        hidden
        onChange={handleImageChange}
        ref={inputRef}
      />
      <ImagePreview
        open={previewOpen}
        handleClose={handlePreviewClose}
        src={originalImage || image}
        alt={alt}
      />
      <ImageInputPopover
        deleteDisabled={image?.includes(DEFAULT_ICON_URL)}
        handleDelete={handleDelete}
        handleChange={handleChange}
        handleImagesOpen={handleImagesOpen}
        anchorRef={anchorRef}
        handleClose={handleClose}
        open={open}
      />
      <StockImagesModal
        setImage={handleImagesSet}
        open={imagesOpen}
        handleClose={handleImagesClose}
      />
    </div>
  )
}

export { ImageInput }
